import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import { adminEventTournamentRoute } from "constants/routes";
import { formatMatchIdentity } from "components/common/matchIdentity";
import { isValidRuleDate } from "./dailyMatchLimits";

const redundantWithoutMatches = new Set(["missing_final", "review_final_format"]);
const isReadyToSchedule = (row) => !row.completed && Number(row.match_count || 0) > 0;

export function visiblePreparationIssues(issues = []) {
  const uniqueIssues = [...new Set(issues)];

  return uniqueIssues.includes("missing_matches")
    ? uniqueIssues.filter((issue) => !redundantWithoutMatches.has(issue))
    : uniqueIssues;
}

export default function SchedulingPreparationSection({
  preparation,
  eventId,
  intl,
  refreshing,
  updating,
  onEditFinal,
  onBulkUpdate,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [matchFilter, setMatchFilter] = useState("ready");
  const [inspectedId, setInspectedId] = useState(null);

  useEffect(() => {
    setSelectedIds([]);
    setConfirmation(null);
    setInspectedId(preparation?.tournaments?.[0]?.tournament_id ?? null);
  }, [preparation]);

  const text = (key, values) => intl.formatMessage({ id: `scheduling.preparation.${key}` }, values);
  const bulk = (key, values) => intl.formatMessage({ id: `scheduling.bulk.${key}` }, values);
  const message = (id, values) => intl.formatMessage({ id }, values);
  const busy = refreshing || updating;
  const tournaments = useMemo(
    () =>
      (preparation?.tournaments || []).map((row) => ({
        ...row,
        issues: visiblePreparationIssues(row.issues),
      })),
    [preparation],
  );
  const preparationIssues = useMemo(() => visiblePreparationIssues(preparation?.issues), [preparation]);
  const issueCount = preparationIssues.length + tournaments.reduce((total, row) => total + row.issues.length, 0);
  const selected = tournaments.filter(
    (row) => selectedIds.includes(row.tournament_id) && !row.completed && row.revision,
  );
  const readyToSchedule = tournaments.filter(isReadyToSchedule).length;
  const withoutMatches = tournaments.filter((row) => Number(row.match_count || 0) === 0).length;

  const filteredTournaments = useMemo(() => {
    if (matchFilter === "ready") return tournaments.filter(isReadyToSchedule);
    if (matchFilter === "without_matches") {
      return tournaments.filter((row) => Number(row.match_count || 0) === 0);
    }

    return tournaments;
  }, [matchFilter, tournaments]);
  const inspected = filteredTournaments.find((row) => row.tournament_id === inspectedId) || filteredTournaments[0];
  const selectableVisibleIds = filteredTournaments
    .filter((row) => !row.completed && row.revision)
    .map((row) => row.tournament_id);
  const allVisibleSelected =
    selectableVisibleIds.length > 0 && selectableVisibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = selectableVisibleIds.some((id) => selectedIds.includes(id));

  const toggleVisibleSelection = (checked) => {
    setSelectedIds((ids) =>
      checked
        ? [...new Set([...ids, ...selectableVisibleIds])]
        : ids.filter((id) => !selectableVisibleIds.includes(id)),
    );
  };

  const prepareBulk = (action) => {
    setConfirmation({ action, start_date: startDate, rows: selected });
  };

  return (
    <Box>
      {!preparation ? (
        <Alert severity="warning">{text("unavailable")}</Alert>
      ) : (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" },
              gap: 2,
              mb: 1,
            }}
          >
            <SummaryCard
              title={text("totalCategories")}
              description={text("totalCategoriesHelp")}
              value={tournaments.length}
            />
            <SummaryCard
              title={text("readyCategories")}
              description={text("readyCategoriesHelp")}
              value={readyToSchedule}
              color="success.main"
            />
            <SummaryCard
              title={text("withoutMatches")}
              description={text("withoutMatchesHelp")}
              value={withoutMatches}
              color={withoutMatches ? "warning.main" : "text.primary"}
            />
            <SummaryCard
              title={text("suggestions")}
              description={text("suggestionsHelp")}
              value={issueCount}
              color={issueCount ? "warning.main" : "text.primary"}
            />
          </Box>
          {/* <Typography variant="caption" color="text.secondary" display="block" textAlign="right" sx={{ mb: 2 }}>
            {preparation.checked_at
              ? text("checked", {
                  time: intl.formatDate(preparation.checked_at, {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                })
              : "—"}
          </Typography> */}

          {preparationIssues.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Stack spacing={0.5}>
                {preparationIssues.map((issue) => (
                  <Typography key={issue} variant="body2">
                    {text(issue)}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {onBulkUpdate && (
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
                <Stack
                  direction={{ xs: "column", xl: "row" }}
                  spacing={2}
                  justifyContent="space-between"
                  alignItems={{ xl: "center" }}
                >
                  <Box>
                    <Typography variant="subtitle2">
                      {selected.length ? bulk("selected", { count: selected.length }) : bulk("actionsTitle")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {bulk(selected.length ? "selectionHelp" : "emptySelectionHelp")}
                    </Typography>
                  </Box>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        gap: 1,
                      }}
                    >
                      <TextField
                        type="date"
                        size="small"
                        label={bulk("date")}
                        value={startDate}
                        disabled={busy}
                        InputLabelProps={{ shrink: true }}
                        onChange={(event) => setStartDate(event.target.value)}
                      />
                      <Button
                        variant="outlined"
                        disabled={busy || !isValidRuleDate(startDate)}
                        onClick={() => prepareBulk("set_start_date")}
                      >
                        {bulk("setDate")}
                      </Button>
                    </Box>
                    <Button
                      color="warning"
                      variant="outlined"
                      disabled={busy || !selected.length}
                      onClick={() => prepareBulk("close_signups")}
                    >
                      {bulk("closeSignups")}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1fr) 340px" },
              gap: 2,
            }}
          >
            <Card variant="outlined" sx={{ minWidth: 0 }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={1.5}
                  alignItems={{ md: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography variant="subtitle1">{text("categoriesTitle")}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {text("tableFilterHelp")}
                    </Typography>
                  </Box>
                  <Box sx={{ maxWidth: "100%", overflowX: "auto", pb: 0.25 }}>
                    <ButtonGroup size="small" aria-label={text("matchFilter")}>
                      {[
                        ["ready", text("readyFilter"), readyToSchedule],
                        ["without_matches", text("withoutMatchesFilter"), withoutMatches],
                        ["all", text("allFilter"), tournaments.length],
                      ].map(([value, label, count]) => (
                        <Button
                          key={value}
                          variant={matchFilter === value ? "contained" : "outlined"}
                          aria-pressed={matchFilter === value}
                          onClick={() => setMatchFilter(value)}
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {label} ({count})
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Box>
                </Stack>
              </Box>
              <TableContainer>
                <Table size="small" sx={{ minWidth: 820 }}>
                  <TableHead>
                    <TableRow>
                      {onBulkUpdate && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={allVisibleSelected}
                            indeterminate={!allVisibleSelected && someVisibleSelected}
                            disabled={busy || !selectableVisibleIds.length}
                            inputProps={{ "aria-label": bulk("selectVisible") }}
                            onChange={(event) => toggleVisibleSelection(event.target.checked)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{text("category")}</TableCell>
                      <TableCell>{text("start")}</TableCell>
                      <TableCell>{text("signups")}</TableCell>
                      <TableCell align="right">{text("generatedMatches")}</TableCell>
                      <TableCell>{text("review")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTournaments.map((row) => {
                      const issues = row.issues || [];

                      return (
                        <TableRow
                          key={row.tournament_id}
                          selected={row.tournament_id === inspected?.tournament_id}
                          hover
                        >
                          {onBulkUpdate && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedIds.includes(row.tournament_id)}
                                disabled={busy || row.completed || !row.revision}
                                inputProps={{ "aria-label": `${bulk("selectForBulk")} ${row.name}` }}
                                onChange={(event) => {
                                  setSelectedIds((ids) =>
                                    event.target.checked
                                      ? [...ids, row.tournament_id]
                                      : ids.filter((selectedId) => selectedId !== row.tournament_id),
                                  );
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                color="inherit"
                                endIcon={<ChevronRightIcon fontSize="small" />}
                                aria-label={text("viewDetails", { name: row.name })}
                                onClick={() => setInspectedId(row.tournament_id)}
                                sx={{ justifyContent: "flex-start", textAlign: "left", px: 0, fontWeight: 700 }}
                              >
                                {row.name}
                              </Button>
                              {row.completed && <Chip size="small" label={text("completed")} />}
                            </Stack>
                          </TableCell>
                          <TableCell>{row.start_date || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={row.open_signup ? "success" : "default"}
                              label={text(row.open_signup ? "open" : "closed")}
                            />
                          </TableCell>
                          <TableCell align="right">{row.match_count ?? "—"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={issues.length ? "warning" : "default"}
                              variant="outlined"
                              clickable
                              aria-label={text("viewReview", { name: row.name })}
                              onClick={() => setInspectedId(row.tournament_id)}
                              label={
                                issues.length ? text("reviewCount", { count: issues.length }) : text("noIssuesShort")
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!filteredTournaments.length && (
                      <TableRow>
                        <TableCell colSpan={onBulkUpdate ? 6 : 5} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>
                            {text("noResults")}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            <CategoryInspector
              row={inspected}
              eventId={eventId}
              busy={busy}
              onEditFinal={onEditFinal}
              text={text}
              message={message}
            />
          </Box>
        </>
      )}

      <Dialog
        open={Boolean(confirmation)}
        onClose={() => {
          if (!updating) setConfirmation(null);
        }}
      >
        <DialogTitle>{bulk("confirm")}</DialogTitle>
        <DialogContent>
          {confirmation && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                {bulk(confirmation.action === "set_start_date" ? "dateHelp" : "closeHelp")}
              </Alert>
              {confirmation.rows.map((row) => (
                <Typography key={row.tournament_id} sx={{ mb: 1 }}>
                  {row.name}:{" "}
                  {confirmation.action === "set_start_date"
                    ? `${row.start_date || "—"} → ${confirmation.start_date}`
                    : `${text(row.open_signup ? "open" : "closed")} → ${text("closed")}`}
                </Typography>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={updating} onClick={() => setConfirmation(null)}>
            {bulk("cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={busy || !confirmation}
            onClick={async () => {
              const payload = {
                action: confirmation.action,
                tournaments: confirmation.rows.map((row) => ({ id: row.tournament_id, revision: row.revision })),
              };
              if (confirmation.action === "set_start_date") payload.start_date = confirmation.start_date;
              if (await onBulkUpdate(payload)) setConfirmation(null);
            }}
          >
            {bulk("apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SummaryCard({ title, description, value, color = "text.primary" }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Typography variant="h4" color={color} sx={{ minWidth: "2ch", lineHeight: 1 }}>
            {value}
          </Typography>
          <Box>
            <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {description}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function CategoryInspector({ row, eventId, busy, onEditFinal, text, message }) {
  if (!row) return null;

  const issues = row.issues || [];
  const finals = row.finals || [];

  return (
    <Card variant="outlined" sx={{ alignSelf: "start", position: { xl: "sticky" }, top: { xl: 16 } }}>
      <CardContent>
        <Typography variant="subtitle1" color="primary.main">
          {text("inspector")}
        </Typography>
        <Typography variant="h4" sx={{ mb: 3, mt: 1 }}>
          {row.name}
        </Typography>
        <Stack spacing={1.5} divider={<Divider flexItem />}>
          <InspectorValue label={text("start")} value={row.start_date || "—"} />
          <InspectorValue label={text("signups")} value={text(row.open_signup ? "open" : "closed")} />
          <InspectorValue label={text("generatedMatches")} value={row.match_count ?? "—"} />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {text("review")}
        </Typography>
        <Stack spacing={1}>
          {issues.map((issue) => (
            <Alert key={issue} severity="warning" icon={false} sx={{ py: 0.5 }}>
              {text(issue)}
            </Alert>
          ))}
          {!row.finals_applicable && <Typography variant="body2">{text("noFinalNeeded")}</Typography>}
          {finals.map((final) => (
            <Button
              key={final.match_id}
              size="small"
              variant="outlined"
              disabled={busy || final.historical || row.completed}
              onClick={() => onEditFinal(final.match_id)}
            >
              {formatMatchIdentity(final, message)} · {text(final.historical ? "historical" : "editFinal")}
            </Button>
          ))}
          {!issues.length && !finals.length && row.finals_applicable && (
            <Typography variant="body2" color="text.secondary">
              {text("noIssues")}
            </Typography>
          )}
        </Stack>

        <Button
          fullWidth
          component={Link}
          to={adminEventTournamentRoute(eventId, row.tournament_uuid)}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          endIcon={<OpenInNewIcon />}
          sx={{ mt: 2 }}
        >
          {text("manageCategory")}
        </Button>
      </CardContent>
    </Card>
  );
}

function InspectorValue({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" textAlign="right">
        {value}
      </Typography>
    </Stack>
  );
}
